import { FC, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  deletePolicyV1,
  getPolicyV1ById,
  patchPolicyV1,
} from '@/lib/services/policy-v1.service';
import useToast from '@/hooks/use-toast.hook';
import useFormHandle from '@/hooks/use-form-handle.hook';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import FormSection from '@/components/form/form-section';
import InputControl from '@/components/form/input-control';
import FormButtons from '@/components/form/form-buttons';
import useQueryHelper from '@/hooks/use-query-helper';
import { useNavigate } from '@tanstack/react-router';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { getAllGroups } from '@/lib/services/group.service';
import { FieldError } from '@/lib/services';

const fields = ['name', 'description', 'groups'];
const formModel = new PolicyModelV1();

interface Props {
  policyId: string;
}

const PolicyProfileForm: FC<Props> = ({ policyId }) => {
  const { errorToast, toast } = useToast();
  const navigate = useNavigate();
  const { canChangePolicy } = useFeature();

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const { patched, error, fieldErrors } = await patchPolicyV1(
        policyId,
        values.propSlice(fields)
      );
      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        toast('The policy was updated');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The policy was not updated');
    },
  });

  const { values } = formHandle;

  const policyQuery = useQuery([QueryKey.PolicyView, policyId], async () =>
    getPolicyV1ById(policyId)
  );

  const handleDelete = () => {
    deletePolicyV1(policyId).then(({ deleted, error }) => {
      if (deleted) {
        toast('The policy was deleted');
        navigate({ to: '/wais/group-policies' });
        return;
      }

      errorToast(error || 'The policy was not deleted');
    });
  };

  const groupsQuery = useQuery([QueryKey.GroupsList], async () => getAllGroups());

  const { data: groups } = groupsQuery;
  const { showLoader: showGroupsLoader } = useQueryHelper(groupsQuery);
  const { showLoader: showPolicyLoader } = useQueryHelper(policyQuery);

  const groupOptions = useMemo(() => {
    if (!groups) {
      return [];
    }

    return groups.map(({ id, name }) => ({
      value: id,
      label: name,
    }));
  }, [groups]);

  if (showPolicyLoader || showGroupsLoader) {
    return;
  }

  return (
    <Form formHandle={formHandle} query={policyQuery}>
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />
      <FormSection title="Profile Settings" scrollId="profile-settings">
        <InputControl
          name="name"
          label="Name"
          formHandle={formHandle}
          readonly={!canChangePolicy}
        />

        <InputControl
          name="description"
          label="Description"
          formHandle={formHandle}
          readonly={!canChangePolicy}
          type="textarea"
        />
      </FormSection>

      <FormSection title="Group Assignment" scrollId="group-assignment">
        <InputControl
          name="groups"
          label="Groups"
          formHandle={formHandle}
          readonly={!canChangePolicy}
          type="groups-list-toggle"
          options={groupOptions}
        />
      </FormSection>

      <FormButtons
        formHandle={formHandle}
        onDelete={handleDelete}
        deleteName={values.name}
        readonly={!canChangePolicy}
      />
    </Form>
  );
};

export default PolicyProfileForm;
